import React, { useEffect, useState, useRef } from 'react';
import * as Icons from 'lucide-react';
import { uploadDeliveryEvidence, completeOrderDelivery, startOrderDelivery, getOrderItems } from '../services/routeService';
import MultiplePaymentEvidenceUpload from './MultiplePaymentEvidenceUpload';
import { computeCollectionAmounts } from '../utils/payments';
import toast from 'react-hot-toast';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;

  // Handle delivery_evidence paths (legacy format for some packaging)
  if (url.includes('delivery_evidence')) {
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    return `/api/public/evidence/${filename}/content`;
  }

  // Handle all uploads/* paths (with or without leading slash)
  if (url.startsWith('/uploads')) return `/api${url}`;
  if (url.startsWith('uploads')) return `/api/${url}`;

  return url;
};

const openImage = (url) => {
  try {
    const formattedUrl = getImageUrl(url);
    const full = formattedUrl.startsWith('http') ? formattedUrl : `${window.location.origin}${formattedUrl}`;
    window.open(full, '_blank', 'noopener');
  } catch (e) {
    window.open(getImageUrl(url), '_blank');
  }
};



// Extrae un posible motivo de "Gestión Especial" desde timeline/context/order
function extractSpecialReason(timeline, order) {
  const ctx = timeline?.context || {};
  const candidates = [
    ctx.special_reason,
    ctx.special_notes,
    ctx.special_management?.reason,
    ctx.special_management?.notes,
    ctx.gestion_especial_motivo,
    ctx.gestion_especial_notas,
    ctx.admin_notes,
    ctx.review_notes,
    order?.special_reason,
    order?.special_notes,
    order?.special_management?.reason,
    order?.special_management?.notes,
    order?.gestion_especial_motivo,
    order?.gestion_especial_notas,
    order?.admin_notes,
    order?.review_notes,
    order?.notes,
    ctx.notes,
  ].filter(Boolean);

  if (candidates.length > 0) return String(candidates[0]);

  // Buscar en eventos si existe un evento de gestión especial
  const ev = (timeline?.events || []).find(
    (e) =>
      e?.type === 'gestion_especial' ||
      /gesti[oó]n especial/i.test(e?.title || '') ||
      /gesti[oó]n especial/i.test(e?.details || '')
  );
  return ev?.details || '';
}

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);

  // Mostrar en zona horaria de Bogotá (UTC-5)
  return d.toLocaleString('es-CO', { timeZone: 'America/Bogota' });
};

const EventItem = ({ event, onPreview }) => {
  let dotColor = 'bg-blue-500';
  if (event.type === 'activity_log') dotColor = 'bg-orange-500';
  if (event.type === 'internal_order') dotColor = 'bg-purple-500';
  if (event.type === 'invoice_created') dotColor = 'bg-green-500';
  if (event.type === 'packaging_evidence') dotColor = 'bg-indigo-500';

  return (
    <div className="relative pl-6 pb-4">
      <div className={`absolute left-0 top-1 w-3 h-3 ${dotColor} rounded-full`}></div>
      <div className="text-xs text-gray-500">{formatDateTime(event.at)}</div>
      <div className="text-sm font-medium text-gray-900">{event.title}</div>
      {event.details && (
        <div className="text-xs text-gray-600 whitespace-pre-wrap">{event.details}</div>
      )}
      {event.location && event.location.lat && event.location.lng && (
        <a
          href={`https://www.google.com/maps?q=${event.location.lat},${event.location.lng}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-bold bg-blue-50 w-fit px-2 py-0.5 rounded"
        >
          <Icons.MapPin className="w-3 h-3" />
          Ver Ubicación exacta
        </a>
      )}
      {Array.isArray(event.attachments) && event.attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {event.attachments.map((att, idx) => (
            <button
              key={idx}
              onClick={() => onPreview(att)}
              title={att.label || 'Ver adjunto'}
              className="relative border rounded overflow-hidden hover:shadow focus:outline-none"
            >
              <img
                src={getImageUrl(att.url)}
                alt={att.label || 'adjunto'}
                className="w-24 h-24 object-cover"
                onError={(e) => {
                  console.error('Failed to load image:', att.url);
                  // Show a visible fallback instead of just opacity
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent && !parent.querySelector('.img-fallback')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'img-fallback w-24 h-24 bg-gray-200 flex items-center justify-center text-xs';
                    fallback.innerHTML = '<span class="text-gray-600">📄<br/>Click<br/>para ver</span>';
                    parent.insertBefore(fallback, parent.firstChild);
                  }
                }}
              />
              <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-[10px] text-white px-1 py-0.5 truncate">
                {att.source || 'adjunto'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
const OrderTimelineModal = ({ isOpen, onClose, order, initialMode = 'history', initialStep = 1, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [preview, setPreview] = useState(null); // { url, label, source }
  const [mode, setMode] = useState(initialMode); // 'history' or 'delivery'
  const [step, setStep] = useState(initialStep); // 1: Evidence, 1.5: Item check, 2: Payment

  // Delivery States
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [amountReceived, setAmountReceived] = useState('');

  const [mixedCashAmount, setMixedCashAmount] = useState('');
  const [mixedTransferAmount, setMixedTransferAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [coords, setCoords] = useState(null); // { latitude, longitude }
  const [childOrders, setChildOrders] = useState([]); // Child INT orders
  // Trusted Delivery States
  const [trustedDelivery, setTrustedDelivery] = useState(false);
  const [authorizedByName, setAuthorizedByName] = useState('');
  const [trustNote, setTrustNote] = useState('');
  // 🆕 Partial delivery states
  const [orderItems, setOrderItems] = useState([]);
  const [rejectedItemsMap, setRejectedItemsMap] = useState({}); // { itemId: { rejected: bool, reason, note } }
  const [loadingItems, setLoadingItems] = useState(false);
  const [adjustedTotal, setAdjustedTotal] = useState(null);

  const fileInputRef = useRef(null);

  const [isZoomed, setIsZoomed] = useState(false);

  // Computed values
  const { totalDue: finalCollection } = order ? computeCollectionAmounts(order) : { totalDue: 0 };
  const requiresPayment = finalCollection > 0;

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setStep(initialStep || 1);
      setPhoto(null);
      setPhotoPreview(null);

      setDeliveryNotes('');
      setTrustedDelivery(false);
      setAuthorizedByName('');
      setTrustNote('');
      if (initialStep === 2) {
        setAmountReceived(finalCollection);
      }
    }
  }, [isOpen, initialMode, initialStep, finalCollection]);

  const handlePreviewAttachment = (att) => {
    if (!att) return;
    setPreview(att);
    setIsZoomed(false);
  };

  // Keyboard navigation for preview
  useEffect(() => {
    if (!preview || attachments.length <= 1) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        const currentIndex = attachments.findIndex(a => a.url === preview.url);
        if (currentIndex !== -1) {
          const prevIndex = (currentIndex - 1 + attachments.length) % attachments.length;
          setPreview(attachments[prevIndex]);
          setIsZoomed(false);
        }
      } else if (e.key === 'ArrowRight') {
        const currentIndex = attachments.findIndex(a => a.url === preview.url);
        if (currentIndex !== -1) {
          const nextIndex = (currentIndex + 1) % attachments.length;
          setPreview(attachments[nextIndex]);
          setIsZoomed(false);
        }
      } else if (e.key === 'Escape') {
        setPreview(null);
        setIsZoomed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [preview, attachments]);

  useEffect(() => {
    const fetchTimeline = async () => {
      if (!isOpen || !order?.id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/orders/${order.id}/timeline`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || `Error ${res.status}`);
        }
        const data = await res.json();
        setTimeline(data.data);

        // Aggregate all attachments for navigation
        const allAttachments = [];
        const seenUrls = new Set();

        // Helper to add
        const add = (list) => {
          if (!Array.isArray(list)) return;
          list.forEach(att => {
            if (att && att.url && !seenUrls.has(att.url)) {
              seenUrls.add(att.url);
              allAttachments.push(att);
            }
          });
        };

        // 1. Top level attachments
        add(data.data?.attachments);

        // 2. Event attachments
        if (Array.isArray(data.data?.events)) {
          data.data.events.forEach(ev => {
            add(ev.attachments);
          });
        }

        // 3. Payment Evidences (Separate endpoint)
        try {
          const evRes = await fetch(`/api/wallet/payment-evidences/${order.id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (evRes.ok) {
            const evData = await evRes.json();
            if (Array.isArray(evData.data)) {
              evData.data.forEach(ev => {
                // Map payment evidence to attachment format
                const att = {
                  url: ev.file_path, // api prefix handled by getImageUrl
                  label: 'Comprobante de Pago',
                  source: `Subido por: ${ev.uploaded_by_name || 'Usuario'}`
                };
                add([att]);
              });
            }
          }
        } catch (evErr) {
          console.warn('Error fetching payment evidences for timeline:', evErr);
          // Non-critical, continue
        }

        setAttachments(allAttachments);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTimeline();
  }, [isOpen, order?.id]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const getCurrentGPS = () => {
    return new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        reject(new Error("Tu dispositivo no soporta geolocalización."));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos.coords),
        (err) => {
          console.error('GPS Error:', err);
          let msg = "No se pudo obtener la ubicación.";
          if (err.code === 1) msg = "Permiso de GPS denegado. Actívalo para continuar.";
          else if (err.code === 2) msg = "Ubicación no disponible. Verifica tu señal GPS.";
          else if (err.code === 3) msg = "Tiempo de espera agotado al obtener GPS.";
          reject(new Error(msg));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const handleUploadEvidence = async () => {
    if (!photo) {
      toast.error('Debes tomar una foto de la entrega');
      return;
    }

    try {
      setIsUploading(true);

      // Capturar GPS — recomendado pero NO bloquea la subida de evidencia
      // GPS será obligatorio en el paso final (handleCompleteDeliveryInternal)
      let currentCoords = null;
      try {
        currentCoords = await getCurrentGPS();
        setCoords({ latitude: currentCoords.latitude, longitude: currentCoords.longitude });
      } catch (gpsErr) {
        console.warn('⚠️ GPS no disponible para evidencia:', gpsErr.message);
        toast('📍 GPS no disponible. La foto se subirá sin ubicación. Activa el GPS para el paso final.', { icon: '⚠️', duration: 4000 });
        // Continuar sin GPS — se intentará de nuevo al completar entrega
      }

      const skipStart = ['in_delivery', 'delivered', 'entregado'].includes(order.messenger_status || order.delivery_status);
      if (!skipStart) {
        await startOrderDelivery(order.id);
      }

      const formData = new FormData();
      formData.append('photo', photo);
      formData.append('description', `Entrega pedido #${order.order_number}`);
      if (currentCoords) {
        formData.append('gps_latitude', currentCoords.latitude);
        formData.append('gps_longitude', currentCoords.longitude);
      }

      await uploadDeliveryEvidence(order.id, formData);
      toast.success('Evidencia subida');

      // Always go to item check step (1.5) first
      setStep(1.5);
      // Load order items for partial delivery check
      try {
        setLoadingItems(true);
        const itemsRes = await getOrderItems(order.id);
        if (itemsRes.success && itemsRes.data?.items) {
          setOrderItems(itemsRes.data.items);
        }
      } catch (itemsErr) {
        console.warn('Could not load items, skipping item check:', itemsErr);
        // Fallback: skip to payment or complete
        if (requiresPayment) {
          setStep(2);
          setAmountReceived(finalCollection);
        } else {
          await handleCompleteDeliveryInternal(currentCoords ? { latitude: currentCoords.latitude, longitude: currentCoords.longitude } : null);
        }
      } finally {
        setLoadingItems(false);
      }
    } catch (error) {
      console.error('Error evidence:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al subir evidencia';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setIsUploading(false);
    }
  };

  // 🆕 Handle proceeding from item check step
  const handleProceedFromItemCheck = () => {
    // Calculate adjusted total
    const rejectedAmount = orderItems.reduce((sum, item) => {
      const ri = rejectedItemsMap[item.id];
      if (ri?.rejected) {
        return sum + Number(item.price || 0);
      }
      return sum;
    }, 0);
    const newTotal = Math.max(0, finalCollection - rejectedAmount);
    setAdjustedTotal(rejectedAmount > 0 ? newTotal : null);

    if (newTotal > 0 && requiresPayment) {
      setStep(2);
      setAmountReceived(newTotal);
    } else {
      // No payment needed (all rejected or fully paid already)
      handleCompleteDeliveryInternal(coords);
    }
  };

  const handleCompleteDeliveryInternal = async (overrideCoords = null) => {
    try {
      setIsSubmitting(true);

      // Capturar GPS si no lo tenemos — OBLIGATORIO
      let finalCoords = overrideCoords || coords;
      if (!finalCoords) {
        try {
          const freshCoords = await getCurrentGPS();
          finalCoords = { latitude: freshCoords.latitude, longitude: freshCoords.longitude };
          setCoords(finalCoords);
        } catch (gpsErr) {
          console.warn('⚠️ GPS no disponible al completar:', gpsErr.message);
          toast.error('📍 Debes activar el GPS para completar la entrega.', { duration: 5000 });
          setIsSubmitting(false);
          return;
        }
      }

      let finalPaymentCollected = 0;
      let finalTransferAmount = 0;
      const isTrusted = trustedDelivery && paymentMethod === 'transferencia';

      // Validate trusted delivery fields
      if (isTrusted) {
        if (!authorizedByName || authorizedByName.trim().length < 3) {
          toast.error('Ingresa el nombre de quien autoriza la entrega con confianza');
          setIsSubmitting(false);
          return;
        }
        if (!trustNote || trustNote.trim().length < 3) {
          toast.error('Ingresa una nota/motivo para la entrega con confianza');
          setIsSubmitting(false);
          return;
        }
      }

      if (step === 2) {
        if (paymentMethod === 'efectivo') {
          finalPaymentCollected = parseFloat(amountReceived);
        } else if (paymentMethod === 'transferencia') {
          finalTransferAmount = isTrusted ? 0 : finalCollection;
        } else if (paymentMethod === 'mixto') {
          finalPaymentCollected = parseFloat(mixedCashAmount);
          finalTransferAmount = parseFloat(mixedTransferAmount);
        }
      }

      // Payment evidences are now uploaded via MultiplePaymentEvidenceUpload component

      const deliveryData = {
        paymentCollected: isTrusted ? 0 : finalPaymentCollected,
        transferAmount: isTrusted ? (adjustedTotal !== null ? adjustedTotal : finalCollection) : finalTransferAmount,
        paymentMethod: step === 2 ? paymentMethod : null,
        deliveryNotes: deliveryNotes || 'Entrega completada vía App',
        isPendingEvidence: false,
        latitude: finalCoords?.latitude,
        longitude: finalCoords?.longitude,
        trustedDelivery: isTrusted,
        authorizedByName: isTrusted ? authorizedByName.trim() : null,
        trustNote: isTrusted ? trustNote.trim() : null,
        // 🆕 Partial delivery: send rejected items to backend
        rejectedItems: Object.entries(rejectedItemsMap)
          .filter(([_, v]) => v?.rejected)
          .map(([itemId, v]) => ({
            orderItemId: Number(itemId),
            quantity: orderItems.find(i => i.id === Number(itemId))?.quantity || 1,
            reason: v.reason || 'cliente_no_quiere',
            note: v.note || null,
            productName: orderItems.find(i => i.id === Number(itemId))?.name || null
          }))
      };

      await completeOrderDelivery(order.id, deliveryData);
      toast.success('¡Entregado!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error completing delivery:', error);
      // CRITICAL: Show actual backend error message instead of generic toast
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al completar entrega';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Icons.History className={`w-5 h-5 ${mode === 'history' ? 'text-blue-600' : 'text-gray-400'}`} />
              <button
                onClick={() => setMode('history')}
                className={`text-sm font-bold ${mode === 'history' ? 'text-blue-700 underline' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Historial
              </button>
            </div>
            {order?.messenger_status !== 'delivered' && (
              <div className="flex items-center space-x-2">
                <Icons.Truck className={`w-5 h-5 ${mode === 'delivery' ? 'text-purple-600' : 'text-gray-400'}`} />
                <button
                  onClick={() => setMode('delivery')}
                  className={`text-sm font-bold ${mode === 'delivery' ? 'text-purple-700 underline' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Registrar Entrega
                </button>
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
            <Icons.X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 max-h-[80vh] overflow-y-auto bg-white">
          {mode === 'history' ? (
            <>
              {loading && <div className="text-center text-gray-500 py-8">Cargando...</div>}
              {error && <div className="text-center text-red-600 py-4">{error}</div>}
              {timeline && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100 grid grid-cols-2 gap-y-2 gap-x-4 text-[13px]">
                      <div><span className="text-gray-500">Pedido:</span> <span className="font-bold text-gray-900">#{timeline.context?.order_number}</span></div>
                      <div><span className="text-gray-500">Cliente:</span> <span className="font-bold text-gray-900">{timeline.context?.customer_name}</span></div>
                      <div><span className="text-gray-500">Entrega:</span> <span className="font-bold text-gray-900">{timeline.context?.delivery_method}</span></div>
                      <div><span className="text-gray-500">Pago:</span> <span className="font-bold text-gray-900">{timeline.context?.payment_method}</span></div>
                      <div className="col-span-2 pt-2 mt-2 border-t border-gray-200">
                        <div className="flex justify-between items-center bg-blue-50 p-2 rounded-lg">
                          <span className="text-blue-800 font-bold">TOTAL A PAGAR:</span>
                          <span className="text-blue-900 font-black text-lg">${Number(parseFloat(timeline.context?.total_amount || 0) + parseFloat(timeline.context?.internal_orders_total || 0)).toLocaleString('es-CO')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1 relative before:absolute before:left-[5.5px] before:top-2 before:bottom-2 before:w-[1px] before:bg-gray-100">
                      {timeline.events?.length ? (
                        timeline.events.map((ev, idx) => (
                          <EventItem key={idx} event={ev} onPreview={handlePreviewAttachment} />
                        ))
                      ) : (
                        <div className="text-center text-gray-400 py-8">Sin eventos registrados</div>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-1 space-y-4">
                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                      <div className="flex items-center gap-2 mb-3 text-gray-900 font-bold text-sm">
                        <Icons.Image className="w-4 h-4 text-blue-600" />
                        Adjuntos
                      </div>
                      {attachments.length === 0 ? (
                        <div className="text-xs text-gray-400 italic">No hay imágenes adjuntas</div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2">
                          {attachments.map((att, idx) => (
                            <button key={idx} onClick={() => handlePreviewAttachment(att)} className="relative aspect-square border rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all">
                              <img src={getImageUrl(att.url)} alt="adjunto" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="max-w-md mx-auto py-4">
              {/* Order Number Badge */}
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl">
                  <Icons.Package className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-black text-blue-900">
                    Pedido #{order?.order_number || order?.orderNumber || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Child Orders (INT) Section */}
              {order?.internal_order_numbers && order.internal_order_numbers.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Icons.Star className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                      Productos Especiales (INT)
                    </span>
                  </div>

                  {(() => {
                    const intNumbers = String(order.internal_order_numbers || '').split(',').filter(Boolean);
                    const intIds = String(order.internal_order_ids || '').split(',').filter(Boolean);
                    const internalTotal = Number(order.internal_orders_total || 0);

                    return intNumbers.map((number, idx) => (
                      <div key={idx} className="mb-2 last:mb-0 p-2 bg-white rounded-lg border border-amber-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-gray-700">
                            {number.trim()}
                          </span>
                          <span className="text-xs font-black text-amber-700">
                            ${(internalTotal / intNumbers.length).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Productos especiales o adicionales
                        </div>
                      </div>
                    ));
                  })()}

                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-amber-900">
                        Subtotal INT
                      </span>
                      <span className="text-xs font-black text-amber-700">
                        ${Number(order.internal_orders_total || 0).toLocaleString('es-CO')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Combined Total Section */}
              <div className="mb-4 p-4 bg-green-50 border-2 border-green-300 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-green-900">
                    💰 Saldo por Cobrar
                  </span>
                  <span className="text-2xl font-black text-green-700">
                    ${finalCollection.toLocaleString('es-CO')}
                  </span>
                </div>

                {/* Breakdown if child orders exist */}
                {order?.internal_orders_total && Number(order.internal_orders_total) > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-200 space-y-1">
                    <div className="text-xs font-semibold text-green-800 mb-2">
                      Desglose:
                    </div>
                    <div className="flex justify-between text-xs text-green-700">
                      <span>Pedido Principal ({order.order_number})</span>
                      <span className="font-semibold">${Number(order.total_amount || 0).toLocaleString('es-CO')}</span>
                    </div>
                    <div className="flex justify-between text-xs text-green-700">
                      <span>Productos Especiales (INT)</span>
                      <span className="font-semibold">+${Number(order.internal_orders_total || 0).toLocaleString('es-CO')}</span>
                    </div>
                    <div className="flex justify-between text-xs text-green-700 pt-2 border-t border-green-200">
                      <span className="font-bold">Total Pedido</span>
                      <span className="font-black">${(Number(order.total_amount || 0) + Number(order.internal_orders_total || 0)).toLocaleString('es-CO')}</span>
                    </div>
                    <div className="flex justify-between text-xs text-green-700">
                      <span>Ya Pagado</span>
                      <span className="font-semibold">-${(Number(order.paid_amount || 0) + (order.internal_orders || []).reduce((sum, io) => sum + Number(io.paidAmount || io.paid_amount || 0), 0)).toLocaleString('es-CO')}</span>
                    </div>
                  </div>
                )}
              </div>

              {step === 1 ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Icons.Camera className="w-8 h-8 text-purple-600" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900">Evidencia de Entrega</h4>
                    <p className="text-sm text-gray-500">Captura una foto para confirmar la entrega</p>
                  </div>

                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-video border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors overflow-hidden relative"
                  >
                    {photoPreview ? (
                      <img src={photoPreview} className="w-full h-full object-cover" alt="preview" />
                    ) : (
                      <>
                        <Icons.Upload className="w-10 h-10 text-gray-300 mb-2" />
                        <span className="text-sm font-bold text-purple-600">Tomar o subir foto</span>
                      </>
                    )}
                    <input type="file" ref={fileInputRef} accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2 ml-1">Nota de entrega (opcional)</label>
                    <textarea
                      value={deliveryNotes}
                      onChange={(e) => setDeliveryNotes(e.target.value)}
                      placeholder="Ej: Recibido por portería, cliente feliz..."
                      className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none h-20 resize-none"
                    />
                  </div>

                  <button
                    onClick={handleUploadEvidence}
                    disabled={!photo || isUploading}
                    className="w-full py-4 bg-purple-600 text-white rounded-xl font-black shadow-lg hover:bg-purple-700 disabled:bg-gray-200 transition-all flex items-center justify-center gap-2"
                  >
                    {isUploading ? <Icons.Loader2 className="w-5 h-5 animate-spin" /> : <Icons.CheckCircle className="w-5 h-5" />}
                    Siguiente: Verificar Productos
                  </button>
                </div>
              ) : step === 1.5 ? (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Icons.ClipboardList className="w-7 h-7 text-amber-600" />
                    </div>
                    <h4 className="text-base font-bold text-gray-900">¿El cliente recibió todo?</h4>
                    <p className="text-xs text-gray-500">Desmarca los productos que el cliente NO aceptó</p>
                  </div>

                  {loadingItems ? (
                    <div className="text-center py-6">
                      <Icons.Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
                      <p className="text-xs text-gray-400 mt-2">Cargando productos...</p>
                    </div>
                  ) : orderItems.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">No se encontraron productos</p>
                      <button
                        onClick={handleProceedFromItemCheck}
                        className="mt-3 px-6 py-2 bg-green-600 text-white rounded-xl text-sm font-bold"
                      >
                        Continuar
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {orderItems.map(item => {
                          const ri = rejectedItemsMap[item.id];
                          const isRejected = ri?.rejected === true;
                          return (
                            <div key={item.id} className={`p-3 rounded-xl border-2 transition-all ${isRejected
                              ? 'border-red-300 bg-red-50'
                              : 'border-green-200 bg-green-50/50'
                              }`}>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRejectedItemsMap(prev => ({
                                      ...prev,
                                      [item.id]: {
                                        ...prev[item.id],
                                        rejected: !isRejected,
                                        reason: prev[item.id]?.reason || 'cliente_no_quiere'
                                      }
                                    }));
                                  }}
                                  className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${isRejected
                                    ? 'border-red-400 bg-red-500'
                                    : 'border-green-400 bg-green-500'
                                    }`}
                                >
                                  {isRejected
                                    ? <Icons.X className="w-4 h-4 text-white" />
                                    : <Icons.Check className="w-4 h-4 text-white" />
                                  }
                                </button>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-bold truncate ${isRejected ? 'text-red-700 line-through' : 'text-gray-900'}`}>
                                    {item.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Cant: {item.quantity} &middot; ${Number(item.price || 0).toLocaleString('es-CO')}
                                  </p>
                                </div>
                                {isRejected && (
                                  <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">Rechazado</span>
                                )}
                              </div>
                              {isRejected && (
                                <div className="mt-2 pl-9">
                                  <select
                                    value={ri?.reason || 'cliente_no_quiere'}
                                    onChange={(e) => setRejectedItemsMap(prev => ({
                                      ...prev,
                                      [item.id]: { ...prev[item.id], reason: e.target.value }
                                    }))}
                                    className="w-full text-xs px-2 py-1.5 border border-red-200 rounded-lg bg-white focus:ring-1 focus:ring-red-400 outline-none"
                                  >
                                    <option value="cliente_no_quiere">Cliente no lo quiere</option>
                                    <option value="producto_danado">Producto dañado</option>
                                    <option value="producto_equivocado">Producto equivocado</option>
                                    <option value="otro">Otro motivo</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Summary */}
                      {(() => {
                        const rejectedCount = Object.values(rejectedItemsMap).filter(r => r?.rejected).length;
                        const rejectedAmount = orderItems.reduce((sum, item) => {
                          return rejectedItemsMap[item.id]?.rejected ? sum + Number(item.price || 0) : sum;
                        }, 0);
                        const newTotal = Math.max(0, finalCollection - rejectedAmount);
                        return rejectedCount > 0 ? (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                            <div className="flex justify-between text-xs text-amber-800 font-bold">
                              <span>Productos rechazados: {rejectedCount}</span>
                              <span className="text-red-600">-${Math.round(rejectedAmount).toLocaleString('es-CO')}</span>
                            </div>
                            <div className="flex justify-between text-sm text-amber-900 font-black mt-1 pt-1 border-t border-amber-200">
                              <span>Nuevo total a cobrar:</span>
                              <span>${Math.round(newTotal).toLocaleString('es-CO')}</span>
                            </div>
                          </div>
                        ) : null;
                      })()}

                      <button
                        onClick={handleProceedFromItemCheck}
                        className="w-full py-4 bg-green-600 text-white rounded-xl font-black shadow-lg hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                      >
                        <Icons.CheckCircle className="w-5 h-5" />
                        {requiresPayment ? 'Siguiente: Registrar Pago' : 'Confirmar Entrega'}
                      </button>

                      <button
                        onClick={() => setStep(1)}
                        className="w-full py-2 text-[10px] font-black text-gray-400 uppercase hover:text-gray-600 transition-colors"
                      >
                        Volver a Evidencia
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-2xl border border-green-100 mb-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white shadow-lg shadow-green-200">
                          <Icons.DollarSign className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-green-700 uppercase tracking-widest">Saldo por Cobrar</p>
                          <p className="text-xl font-black text-gray-900">${finalCollection.toLocaleString('es-CO')}</p>
                        </div>
                      </div>
                      {paymentMethod === 'mixto' && (
                        <div className="text-right">
                          <p className="text-[10px] font-black text-gray-400 uppercase">Restante</p>
                          <p className={`text-sm font-black ${(parseFloat(mixedCashAmount || 0) + parseFloat(mixedTransferAmount || 0)) === finalCollection ? 'text-green-600' : 'text-orange-500'}`}>
                            ${(finalCollection - (parseFloat(mixedCashAmount || 0) + parseFloat(mixedTransferAmount || 0))).toLocaleString('es-CO')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex p-1.5 bg-gray-100 rounded-2xl">
                      {['efectivo', 'transferencia', 'mixto'].map(m => (
                        <button
                          key={m}
                          onClick={() => setPaymentMethod(m)}
                          className={`flex-1 py-3 text-[11px] font-black rounded-xl transition-all capitalize flex items-center justify-center gap-2 ${paymentMethod === m
                            ? 'bg-white text-gray-900 shadow-md transform scale-[1.02]'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                          {m === 'efectivo' && <Icons.Banknote className="w-4 h-4" />}
                          {m === 'transferencia' && <Icons.Send className="w-4 h-4" />}
                          {m === 'mixto' && <Icons.Zap className="w-4 h-4" />}
                          {m}
                        </button>
                      ))}
                    </div>

                    {paymentMethod === 'efectivo' && (
                      <div className="bg-white p-5 rounded-2xl border-2 border-green-100 shadow-sm focus-within:border-green-500 transition-colors">
                        <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 tracking-widest">Monto Recibido en Efectivo</label>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl font-black text-gray-400">$</span>
                          <input
                            type="number"
                            value={amountReceived}
                            onChange={(e) => setAmountReceived(e.target.value)}
                            className="w-full text-3xl font-black bg-transparent outline-none text-gray-900 placeholder-gray-200"
                            placeholder="0"
                            autoFocus
                          />
                        </div>
                      </div>
                    )}

                    {paymentMethod === 'mixto' && (
                      <div className="space-y-3 mb-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white p-4 rounded-2xl border-2 border-gray-100 shadow-sm focus-within:border-green-500 transition-all">
                            <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 tracking-widest">Efectivo 💵</label>
                            <input
                              type="number"
                              value={mixedCashAmount}
                              onChange={(e) => {
                                const val = e.target.value;
                                setMixedCashAmount(val);
                                if (val !== '' && !isNaN(val)) {
                                  const remaining = Math.max(0, finalCollection - parseFloat(val));
                                  setMixedTransferAmount(remaining.toFixed(0));
                                }
                              }}
                              className="w-full text-2xl font-black text-gray-900 bg-transparent outline-none"
                              placeholder="0"
                            />
                          </div>
                          <div className="bg-white p-4 rounded-2xl border-2 border-gray-100 shadow-sm focus-within:border-blue-500 transition-all">
                            <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 tracking-widest">Transf. 📱</label>
                            <input
                              type="number"
                              value={mixedTransferAmount}
                              onChange={(e) => {
                                const val = e.target.value;
                                setMixedTransferAmount(val);
                                if (val !== '' && !isNaN(val)) {
                                  const remaining = Math.max(0, finalCollection - parseFloat(val));
                                  setMixedCashAmount(remaining.toFixed(0));
                                }
                              }}
                              className="w-full text-2xl font-black text-gray-900 bg-transparent outline-none"
                              placeholder="0"
                            />
                          </div>
                        </div>

                        {/* Indicador de Balance Dinámico */}
                        <div className={`p-3 rounded-xl flex items-center justify-between transition-all ${Math.abs((parseFloat(mixedCashAmount || 0) + parseFloat(mixedTransferAmount || 0)) - finalCollection) < 1
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                          }`}>
                          <div className="flex items-center gap-2">
                            {Math.abs((parseFloat(mixedCashAmount || 0) + parseFloat(mixedTransferAmount || 0)) - finalCollection) < 1 ? (
                              <Icons.CheckCircle className="w-4 h-4" />
                            ) : (
                              <Icons.AlertCircle className="w-4 h-4 animate-pulse" />
                            )}
                            <span className="text-[10px] font-black uppercase tracking-widest">
                              {Math.abs((parseFloat(mixedCashAmount || 0) + parseFloat(mixedTransferAmount || 0)) - finalCollection) < 1
                                ? 'Monto Cuadrado Perfecto'
                                : `Diferencia: $${(finalCollection - (parseFloat(mixedCashAmount || 0) + parseFloat(mixedTransferAmount || 0))).toLocaleString()}`
                              }
                            </span>
                          </div>
                          <span className="text-xs font-black">
                            Total: ${(parseFloat(mixedCashAmount || 0) + parseFloat(mixedTransferAmount || 0)).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}

                    {(paymentMethod === 'transferencia' || paymentMethod === 'mixto') && (
                      <div className="space-y-3">
                        {/* Entrega con Confianza - Solo para transferencia */}
                        {paymentMethod === 'transferencia' && (
                          <>
                            <label
                              className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${trustedDelivery
                                ? 'border-amber-400 bg-amber-50 shadow-sm'
                                : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/50'
                                }`}
                              onClick={() => setTrustedDelivery(!trustedDelivery)}
                            >
                              <div className="flex items-center h-5 mt-0.5">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${trustedDelivery
                                  ? 'bg-amber-500 border-amber-500'
                                  : 'border-gray-300 bg-white'
                                  }`}>
                                  {trustedDelivery && <Icons.Check className="w-3.5 h-3.5 text-white" />}
                                </div>
                              </div>
                              <div>
                                <span className="font-black text-sm text-gray-900 flex items-center gap-1.5">
                                  <Icons.ShieldCheck className="w-4 h-4 text-amber-600" />
                                  Entrega Con Confianza
                                </span>
                                <span className="text-[11px] text-gray-500 block mt-0.5">
                                  El cliente promete pagar después o el banco está caído
                                </span>
                              </div>
                            </label>

                            {trustedDelivery && (
                              <div className="space-y-2 pl-3 border-l-3 border-amber-400 ml-2 animate-in slide-in-from-top-2">
                                <input
                                  type="text"
                                  value={authorizedByName}
                                  onChange={(e) => setAuthorizedByName(e.target.value)}
                                  placeholder="¿Quién autoriza? (Nombre)"
                                  className="w-full px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-400 outline-none transition-all"
                                />
                                <input
                                  type="text"
                                  value={trustNote}
                                  onChange={(e) => setTrustNote(e.target.value)}
                                  placeholder="Nota / Motivo (ej: paga hoy en la tarde)"
                                  className="w-full px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-400 outline-none transition-all"
                                />
                              </div>
                            )}
                          </>
                        )}

                        {/* Soportes de Transferencia - ocultar si es entrega con confianza */}
                        {!trustedDelivery && (
                          <div>
                            <label className="text-[10px] font-black text-gray-400 uppercase block ml-1 tracking-widest mb-2">Soportes de Transferencia</label>
                            <MultiplePaymentEvidenceUpload
                              orderId={order.id}
                              showExistingEvidences={true}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={handleCompleteDeliveryInternal}
                      disabled={isSubmitting || (paymentMethod === 'mixto' && Math.abs((parseFloat(mixedCashAmount || 0) + parseFloat(mixedTransferAmount || 0)) - finalCollection) >= 1)}
                      className="w-full py-4 bg-green-600 text-white rounded-2xl font-black shadow-xl hover:bg-green-700 disabled:bg-gray-200 disabled:shadow-none transition-all flex items-center justify-center gap-3 mt-4"
                    >
                      {isSubmitting ? <Icons.Loader2 className="w-5 h-5 animate-spin" /> : <Icons.CheckCircle className="w-6 h-6" />}
                      <span className="text-sm uppercase tracking-widest">Finalizar y Registrar Pago</span>
                    </button>

                    <button
                      onClick={() => setStep(1)}
                      className="w-full py-2 text-[10px] font-black text-gray-400 uppercase hover:text-gray-600 transition-colors"
                    >
                      Volver a Evidencia
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Modal de previsualización de adjunto */}
        {preview && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-80" onClick={(e) => {
            if (e.target === e.currentTarget) setPreview(null);
          }}>
            {/* Navigation Buttons (Left) */}
            {attachments.length > 1 && (
              <button
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 p-2 focus:outline-none z-[70]"
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = attachments.findIndex(a => a.url === preview.url);
                  if (currentIndex !== -1) {
                    const prevIndex = (currentIndex - 1 + attachments.length) % attachments.length;
                    setPreview(attachments[prevIndex]);
                  }
                }}
              >
                <Icons.ChevronLeft className="w-10 h-10" />
              </button>
            )}

            <div className="bg-white rounded-lg shadow-lg max-w-[95vw] w-auto overflow-hidden flex flex-col max-h-[95vh]" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-3 py-2 border-b shrink-0">
                <div className="text-sm font-semibold truncate pr-2">
                  {preview.label || 'Adjunto'}
                  {attachments.length > 1 && (
                    <span className="text-gray-500 font-normal ml-2">
                      ({attachments.findIndex(a => a.url === preview.url) + 1} / {attachments.length})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openImage(preview.url)} className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded">Abrir en pestaña</button>
                  <button onClick={() => setPreview(null)} className="p-1 text-gray-600 hover:text-gray-900" aria-label="Cerrar">
                    <Icons.X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-0 overflow-auto flex items-center justify-center bg-gray-100 min-h-[200px] relative" style={{ cursor: isZoomed ? 'zoom-out' : 'zoom-in' }} onClick={() => setIsZoomed(!isZoomed)}>
                <img
                  src={getImageUrl(preview.url)}
                  alt={preview.label || 'adjunto'}
                  className={`w-auto max-w-none transition-all duration-200 ${isZoomed ? '' : 'max-h-[85vh]'}`}
                  style={{ minWidth: isZoomed ? 'auto' : 'min(100%, 400px)', width: isZoomed ? 'auto' : 'auto' }}
                />
              </div>
            </div>

            {/* Navigation Buttons (Right) */}
            {attachments.length > 1 && (
              <button
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 p-2 focus:outline-none z-[70]"
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = attachments.findIndex(a => a.url === preview.url);
                  if (currentIndex !== -1) {
                    const nextIndex = (currentIndex + 1) % attachments.length;
                    setPreview(attachments[nextIndex]);
                  }
                }}
              >
                <Icons.ChevronRight className="w-10 h-10" />
              </button>
            )}
          </div>
        )}

        <div className="px-4 py-3 border-t flex justify-end">
          <button onClick={onClose} className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm">Cerrar</button>
        </div>
      </div>
    </div>
  );
};

export default OrderTimelineModal;
